// src/Components/Menu/MenuAttachment.tsx
import React, { useState } from 'react';
import { MenuCategory } from '../../Redux/MenuCategory/menuCategorySlice';
import { MenuItem } from '../../Redux/Menu/menuSlice';
import { formatPrice } from '../../Formatter/formatPrice';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { attachMenuToCategory, detachMenuFromCategory, fetchMenuCategories, reorderMenusInCategory } from '../../Redux/MenuCategory/menuCategoryApi';
import { AppDispatch } from '../../Redux/Store';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useDraggable } from '../../hooks/useDraggable';
import {
  Box,
  FormControl,
  Select,
  MenuItem as MuiMenuItem,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  Button,
  InputLabel,
  Alert,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';

interface MenuAttachmentProps {
  menuCategories: MenuCategory[];
  selectedCategory: string | null;
  setSelectedCategory: React.Dispatch<React.SetStateAction<string | null>>;
  toggleView: 'attached' | 'unattached';
  setToggleView: React.Dispatch<React.SetStateAction<'attached' | 'unattached'>>;
  filteredMenus: MenuItem[];
}

interface MenuItemProps {
  menu: MenuItem;
  onAttach?: (menuId: string) => Promise<void>;
  onDetach?: (menuId: string) => Promise<void>;
  isAttached: boolean;
}

interface DraggableMenuItemProps extends MenuItemProps {
  isDraggable: boolean;
}

const DraggableMenuItemComponent: React.FC<DraggableMenuItemProps> = ({
  menu,
  onAttach,
  onDetach,
  isAttached,
  isDraggable,
}) => {
  const { setNodeRef, setActivatorNodeRef, style, attributes, listeners } = useDraggable(
    menu.id,
    { direction: 'vertical', dragDisabled: !isDraggable }
  );

  if (!isDraggable) {
    return (
      <ListItem
        sx={{
          bgcolor: 'background.paper',
          '&:hover': {
            bgcolor: 'action.hover',
          },
          mb: 1,
          borderRadius: 1,
        }}
      >
        <ListItemText
          primary={menu.name}
          secondary={`¥${formatPrice(menu.price)}`}
        />
        <Button
          onClick={() => isAttached ? onDetach?.(menu.id) : onAttach?.(menu.id)}
          startIcon={isAttached ? <LinkOffIcon /> : <LinkIcon />}
        >
          {isAttached ? '解除' : '紐付け'}
        </Button>
      </ListItem>
    );
  }

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        mb: 1,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        ref={setActivatorNodeRef}
        sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', mr: 2 }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon sx={{ color: 'text.secondary' }} />
      </Box>
      <ListItemText
        primary={menu.name}
        secondary={`¥${formatPrice(menu.price)}`}
        sx={{ flex: 1 }}
      />
      <Button
        onClick={() => isAttached ? onDetach?.(menu.id) : onAttach?.(menu.id)}
        startIcon={isAttached ? <LinkOffIcon /> : <LinkIcon />}
      >
        {isAttached ? '解除' : '紐付け'}
      </Button>
    </ListItem>
  );
};

const MenuAttachment: React.FC<MenuAttachmentProps> = ({
  menuCategories,
  selectedCategory,
  setSelectedCategory,
  toggleView,
  setToggleView,
  filteredMenus,
}) => {
  const { store_id } = useParams<{ store_id: string }>();
  const dispatch: AppDispatch = useDispatch();
  const [hasChanges, setHasChanges] = useState(false);
  const [localMenus, setLocalMenus] = useState<MenuItem[]>(filteredMenus);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 50,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update local menus when filteredMenus changes
  React.useEffect(() => {
    setLocalMenus(filteredMenus);
    setHasChanges(false);
  }, [filteredMenus]);

  const handleAttachMenu = async (menuId: string) => {
    if (!store_id || !selectedCategory) {
      return;
    }
    try {
      const category = menuCategories.find(cat => cat.id === selectedCategory);
      const sortIndex = category?.items?.length ?? 0;

      await dispatch(
        attachMenuToCategory({
          storeId: store_id,
          menuCategoryId: selectedCategory,
          menuId,
          sortIndex,
        })
      ).unwrap();
      await dispatch(fetchMenuCategories(store_id)).unwrap();
      setError(null);
    } catch (error) {
      console.error('メニューの紐付けに失敗しました。', error);
      setError('メニューの紐付けに失敗しました。');
    }
  };

  const handleDetachMenu = async (menuId: string) => {
    if (!store_id || !selectedCategory) {
      return;
    }
    try {
      await dispatch(
        detachMenuFromCategory({
          storeId: store_id,
          menuCategoryId: selectedCategory,
          menuId,
        })
      ).unwrap();
      await dispatch(fetchMenuCategories(store_id)).unwrap();
      setError(null);
    } catch (error) {
      console.error('メニューの紐付け解除に失敗しました。', error);
      setError('メニューの紐付け解除に失敗しました。');
    }
  };

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: 'attached' | 'unattached') => {
    if (newView !== null) {
      setToggleView(newView);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || toggleView !== 'attached') return;

    if (active.id !== over.id) {
      const oldIndex = localMenus.findIndex((menu) => menu.id === active.id);
      const newIndex = localMenus.findIndex((menu) => menu.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedMenus = arrayMove(localMenus, oldIndex, newIndex);
        setLocalMenus(reorderedMenus);
        setHasChanges(true);
      }
    }
  };

  const handleSaveOrder = async () => {
    if (!store_id || !selectedCategory || !hasChanges) return;

    try {
      // Create array of menu items with their new sort indices
      const menusToReorder = localMenus.map((menu, index) => ({
        menuId: menu.id,
        sortIndex: index + 1,
      }));

      await dispatch(
        reorderMenusInCategory({
          storeId: store_id,
          categoryId: selectedCategory,
          menus: menusToReorder,
        })
      ).unwrap();

      await dispatch(fetchMenuCategories(store_id)).unwrap();
      setHasChanges(false);
      setError(null);
    } catch (error) {
      console.error('並び順の保存に失敗しました。', error);
      setError('並び順の保存に失敗しました。');
    }
  };

  const isAttachedView = toggleView === 'attached';
  const menusToDisplay = isAttachedView ? localMenus : filteredMenus;

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>カテゴリを選択</InputLabel>
        <Select
          value={selectedCategory || ''}
          onChange={(e) => setSelectedCategory(e.target.value as string)}
        >
          {menuCategories.map((category) => (
            <MuiMenuItem key={category.id} value={category.id}>
              {category.name}
            </MuiMenuItem>
          ))}
        </Select>
      </FormControl>

      <ToggleButtonGroup
        value={toggleView}
        exclusive
        onChange={handleViewChange}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="attached">
          紐付け済み
        </ToggleButton>
        <ToggleButton value="unattached">
          未紐付け
        </ToggleButton>
      </ToggleButtonGroup>

      {isAttachedView && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveOrder}
            startIcon={<SaveIcon />}
            disabled={!hasChanges}
          >
            並び順を保存
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isAttachedView ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={menusToDisplay.map((menu) => menu.id)}
            strategy={verticalListSortingStrategy}
          >
            <List>
              {menusToDisplay.map((menu) => (
                <DraggableMenuItemComponent
                  key={menu.id}
                  menu={menu}
                  onAttach={handleAttachMenu}
                  onDetach={handleDetachMenu}
                  isAttached={isAttachedView}
                  isDraggable={isAttachedView}
                />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      ) : (
        <List>
          {menusToDisplay.map((menu) => (
            <DraggableMenuItemComponent
              key={menu.id}
              menu={menu}
              onAttach={handleAttachMenu}
              onDetach={handleDetachMenu}
              isAttached={isAttachedView}
              isDraggable={false}
            />
          ))}
        </List>
      )}
    </Box>
  );
};

export default MenuAttachment;
